import { useEffect, useRef, useState } from 'react';
import { useAbility } from '@/providers/';
import { toast } from 'sonner';
import { KeenIcon } from '@/components';
import { Can } from '@casl/react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { useAuthContext } from '@/auth';
import { FiltersToFormData, getAccessToken } from '@/utils';
import { createDataTableOptions, createDataTableConfig } from '@/utils';
import { ConfirmationModal, EmailSendModal } from '@/components/modal';
import { useConfirmationModal } from '@/hooks';
import { goTo403 } from "@/utils/Navigation";
import { useNavigate } from 'react-router';
import crmTemplateService from '@/services/massMailing/templates/crmTemplateService';
import { ColumnToggleDropdown } from '@/components/TableFilter/ColumnToggleDropdown';
import { Send, Edit, Trash, Code, Eye, Copy } from 'lucide-react';
import * as Yup from "yup";
import intakeFormService from '../../services/intake-form/intakeFormService';
import { Container } from '@/components/container';
import { buildEmbedCode } from './embedCode';

DataTable.use(DT);
const IntakeForms = () => {
    const { logout } = useAuthContext();
    const [open, setOpen] = useState(false);
    const [sendFormId, setSendFormId] = useState(null)
    const { ability } = useAbility();
    const API_URL = import.meta.env.VITE_APP_BASE_URL;
    const token = getAccessToken();
    const tableRef = useRef(null);
    const filterRef = useRef({});
    const canDeleteIntakeForm = ability.can('delete', 'intake-form');
    const canEditIntakeForm = ability.can('access', 'intake-form');
    const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();
    const navigate = useNavigate();
    const [emailLoading, setEmailLoading] = useState(false)
    const [html, setHtml] = useState('')

    const emailValidationSchema = Yup.object().shape({
        email: Yup.string()
            .email("Invalid email format")
            .required("Email is required"),
        cc_emails: Yup.array().of(Yup.string().email("Invalid email")),
        subject: Yup.string().max(200, "Subject cannot exceed 200 characters"),
        email_body: Yup.string().max(1000, "Message cannot exceed 1000 characters"),
    });

    const emailInitialValues = {
        email: "",
        cc_emails: '',
        // Prefill a meaningful, editable subject based on the selected form's name.
        subject: sendFormId?.name ? `Please Complete: ${sendFormId.name}` : "",
        email_body: "",
    };

    const columns = [
        // {
        //     title: 'S#',
        //     name: 'serial',
        //     data: null,
        //  orderable: true,se,
        //     render: (data, type, row, meta) => meta.row + 1,
        // },
        {
            data: 'name',
            name: 'name',
            title: 'Form Name',
            render: function (data, type, row) {
                return `<div id="form-name-cell-${row.id}"></div>`;
            }
        },
        {
            data: 'status',
            name: 'status',
            title: 'Status',
            render: function (value) {
                const badgeClass = value === 'active' ? 'badge-success' : 'badge-danger';
                const label = value === 'active' ? 'Active' : 'Inactive';
                return `<span class="badge badge-pill badge-outline ${badgeClass} gap-1 items-center">
            <span class="badge badge-dot size-1.5 ${badgeClass}"></span>
            ${label}
          </span>`
            }
        },
        { data: 'created_at', name: 'created_at', title: 'Created At' },
        {
            title: 'Actions',
            name: 'actions',
            data: null,
            orderable: false,
            searchable: false,
            render: function (data, type, row) {
                return `<div id="action-cell-${row.id}"></div>`;
            }
        }
    ];

    const defaultVisibleColumns = [
        // 'serial',
        'name',
        'status',
        'created_at',
        'actions'
    ];

    const handleSendForm = async (values) => {
        const payload = {
            ...values,
            intake_form_id: sendFormId.id
        }
        // return console.log('values--->', payload, values)
        setEmailLoading(true)
        try {
            const { data, status } = await intakeFormService.sendIntakeForm(payload)
            if (status) {
                toast.success("Form Sent.", {
                    description: "The form was successfully sent to the recipient.",
                });
                closeSendFormModal()
            }
        } catch (error) {
            console.error(error);
            toast.error('Oops! Something went wrong.', {
                description:
                    'An error occurred while downloading document. Please try again in a moment.',
            });
        } finally {
            setEmailLoading(false)
        }
    }

    const openSendFormModal = (row) => {
        console.log(row)
        setSendFormId(row)
        setOpen(true)
    }

    const closeSendFormModal = (row) => {
        setSendFormId(null)
        setOpen(false)
    }
    const buildBaseUrl = () => {
        const origin = window.location.origin; // e.g. http://localhost:3000
        const base = import.meta.env.BASE_URL || "/";   // e.g. /oak/release3/
        return (origin + base).replace(/\/$/, "");
    };

    const handleCopyEmbedCode = async (row) => {
        console.log('row', row)
        try {
            const token = await generateTokenForIframe(row.id);

            const baseUrl = buildBaseUrl();
            const src = `${baseUrl}/public/intake-form?token=${encodeURIComponent(token)}`;

            // The iframe sizes itself to the form (OL-224).
            const iframeCode = buildEmbedCode({
                src,
                title: row?.name,
                origin: window.location.origin,
                frameId: `oak-form-${String(token).slice(0, 8)}`,
            });

            await navigator.clipboard.writeText(iframeCode);
            toast.success("Embed code copied to clipboard.", {
                description: "It sizes itself to the form. Replace any embed code copied before, so the form is never cut off.",
            });
        } catch (error) {
            console.error("Error copying embed code:", error);
            toast.error(error?.message ?? "Failed to generate embed code.");
        }
    };

    console.log('const origin = window.location.origin;', window.location.origin)
    const generateTokenForIframe = async (id) => {
        const res = await intakeFormService.generateTokenForIframe(id);
        console.log('res', res)

        if (!res.data) throw new Error("Token not found in API response.");

        return res?.data;
    };

    const handleDuplicate = async (id) => {
        try {
            const { status, data } = await intakeFormService.duplicateIntakeForm(id);
            if (!status) throw new Error();
            toast.success(`Created "${data.name}".`);
            navigate(`/intake-forms/edit/${data.id}`);
        } catch (error) {
            console.error('Error duplicating intake form:', error);
            toast.error('Could not duplicate the form.');
        }
    };

    const renderCustomActions = (row) => {
        const { id } = row;
        return (
            <DropdownMenu>
                {(canDeleteIntakeForm || canEditIntakeForm) && (
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-gray-600">
                            <KeenIcon icon="dots-vertical" className="text-[18px]" />
                        </Button>
                    </DropdownMenuTrigger>
                )}
                <DropdownMenuContent align="end" className="w-48">
                    <Can I="handle" a="intake-form" ability={ability}>
                        <DropdownMenuItem onClick={() => navigate(`/intake-forms/edit/${id}`)}>
                            <KeenIcon icon="notepad-edit" className="mr-0.5 h-4 w-4 cursor-pointer" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicate(id)}>
                            <Copy className="mr-0.5 h-4 w-4 cursor-pointer" /> Duplicate
                        </DropdownMenuItem>
                    </Can>
                    {canEditIntakeForm && row.status === 'active' && (
                        <DropdownMenuItem onClick={() => { openSendFormModal(row) }}>
                            <Send className="mr-0.5 h-4 w-4 cursor-pointer" /> Send Form
                        </DropdownMenuItem>
                    )}

                    <DropdownMenuItem onClick={() => {
                        const baseUrl = buildBaseUrl();
                        window.open(
                            `${baseUrl}/intake-forms/preview/${id}`,
                            "_blank",
                            "noopener,noreferrer"
                        )
                    }}>
                        <Eye className="mr-0.5 h-4 w-4 cursor-pointer" /> Preview Form
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => handleCopyEmbedCode(row)}>
                        <Code className="mr-0.5 h-4 w-4 cursor-pointer" /> Copy Embed Code
                    </DropdownMenuItem>

                    <Can I="delete" a="intake-form" ability={ability}>
                        <DropdownMenuItem onClick={() => openModal(id)} className="text-red-600 cusror-pointer">
                            <KeenIcon icon="trash" className="mr-0.5 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                    </Can>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    };

    const handleEditRedirect = (id) => {
        window.location.href = `/api/secure-forms/${id}/edit`;
    };


    const ajaxConfig = createDataTableConfig('/intake-form/data_table', filterRef, token, logout);

    // const ajaxConfig = {
    //     method: 'POST',
    //     headers: {
    //         Authorization: `Bearer ${token}`,
    //     },
    //     url: `${API_URL}/intake-form/data_table`,
    //     xhrFields: {
    //         withCredentials: true
    //     },
    //     data(dt) {
    //         const activeFilters = filterRef.current;

    //         FiltersToFormData(activeFilters, dt);

    //         console.log('Final data sent to DataTable:', dt);
    //         return dt;
    //     },
    //     error: function (xhr, error, thrown) {
    //         if (xhr.status === 401) {
    //             logout();
    //         } else if (xhr.status === 403) {
    //             goTo403()
    //         }
    //     }
    // }

    const options = createDataTableOptions({
        // order: [[4, 'desc']],
        order: [[2, 'desc']],
        initComplete: function () {
            const api = this.api();
            api.columns().every(function () {
                const col = this;
                const name = col.settings()[0].aoColumns[col.index()].name;
                if (name && !defaultVisibleColumns.includes(name)) {
                    col.visible(false);
                }
            });
        },
        drawCallback: function () {
            const api = this.api();
            const rows = api.rows({ page: 'current' }).data();

            rows.each((row) => {
                const container = document.getElementById(`action-cell-${row.id}`);
                if (container) {
                    if (!container._reactRoot) {
                        container._reactRoot = createRoot(container);
                    }
                    container._reactRoot.render(renderCustomActions(row));
                }
                const formNameContainer = document.getElementById(`form-name-cell-${row.id}`);
                if (formNameContainer) {
                    if (!formNameContainer._reactRoot) {
                        formNameContainer._reactRoot = createRoot(formNameContainer);
                    }
                    formNameContainer._reactRoot.render(renderFormName(row));
                }
            });
        },
    });

    const refresh = () => {
        const table = tableRef.current.dt();
        table.ajax.reload();
    };

    const handleDelete = async () => {
        closeModal();
        try {
            const { data, status } = await intakeFormService.deleteIntakeForm(actionId);
            if (status) {
                toast.success("Form deleted!", {
                    description: `Form has been deleted successfully and moved to the recycle bin.`,
                });
                refresh();
            }
        } catch (error) {
            console.error('Error Deleting Intake Form', error);
            toast.error('Oops! Something went wrong.', {
                description: 'An error occurred while deleting contact. Please try again in a moment.',

            });
        }
    };

    const previewForm = async (id) => {
        try {
            setHtml('');
            const { data } = await intakeFormService.previewIntakeForm(id);
            setHtml(data);
        } catch (error) {
            console.log('Error Fetching Form Preview', error);
        }
    }

    const openIframeInNewTab = () => {
        console.log('1');
        if (!html) return;

        console.log('2');
        // Open a new blank tab
        const newWindow = window.open("", "_blank");

        // Write your HTML content into the new tab
        newWindow.document.open();
        newWindow.document.write(html);
        newWindow.document.close();
    };

    const renderFormName = (row) => {
        const { id } = row;
        return (
            <button onClick={() => {
                const baseUrl = buildBaseUrl();
                window.open(
                    `${baseUrl}/intake-forms/preview/${id}`,
                    "_blank",
                    "noopener,noreferrer"
                )
            }} className='link text-start'>{row.name}</button>
        )
    }

    useEffect(() => {
        if (html) {
            console.log('html updated:', html);
            openIframeInNewTab();
        }
    }, [html]);

    return (
        <>
            <Container>
                <div className="min-w-full mb-4 card">
                    <div className="card-header">
                        <div className='flex items-center gap-1'>
                            <ColumnToggleDropdown tableRef={tableRef} columns={columns} defaultVisibleColumns={defaultVisibleColumns} />
                            {/* <h3 className="card-title">Intake Forms</h3> */}
                        </div>

                        <div className='flex flex-wrap items-center gap-2'>
                            <Can I="handle" a="intake-form" ability={ability}>
                                <button className='text-xs btn btn-sm btn-light' onClick={() => navigate(`/intake-forms/create`)}>
                                    <KeenIcon icon="plus" className="w-fit btn-icon" />
                                    Add New
                                </button>
                            </Can>
                        </div>
                    </div>


                    <DataTable
                        id="intake-forms-table"
                        ajax={ajaxConfig}
                        columns={columns}
                        options={options}
                        ref={tableRef}
                    />

                    <ConfirmationModal
                        show={isOpen}
                        onClose={closeModal}
                        onConfirm={handleDelete}
                        message="Are you sure you want to delete this Form? You can restore it from the Recycle Bin within 30 days."
                        title="Are You Sure?"
                    />

                    <EmailSendModal
                        title='Send Form To Recipient'
                        open={open}
                        onClose={() => closeSendFormModal()}
                        loading={emailLoading}
                        onSubmit={(values) => handleSendForm(values)}
                        initialValues={emailInitialValues}
                        validationSchema={emailValidationSchema}

                    />
                </div>
            </Container>
        </>
    )
}

export { IntakeForms }
